import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/api";
import MagmaSelect from "../components/MagmaSelect";
import "./Dashboard.css";

const OBJECTIVE_CAROUSEL_SIZE = 3;

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace("_", " ");
}

function isConcluida(value) {
  const normalized = normalizeText(value);
  return normalized === "concluido" || normalized === "concluida";
}

function isAcaoNaoIniciada(value) {
  return normalizeText(value) === "nao iniciado";
}

function isAcaoAndamento(value) {
  return normalizeText(value) === "em andamento";
}

function isRiscoCritico(value) {
  const normalized = normalizeText(value);
  return normalized === "alto" || normalized === "critico";
}

function formatStatusLabel(value) {
  const normalized = normalizeText(value);
  if (normalized === "nao iniciado") return "Não iniciado";
  if (normalized === "em andamento") return "Em andamento";
  if (normalized === "concluido" || normalized === "concluida") return "Concluído";
  return value || "-";
}

function buildConicGradient(items, total) {
  if (total <= 0) {
    return "conic-gradient(#f3d7cf 0deg 360deg)";
  }

  let current = 0;
  const slices = items.map(item => {
    const angle = (item.value / total) * 360;
    const start = current;
    current += angle;
    return `${item.color} ${start}deg ${current}deg`;
  });

  return `conic-gradient(${slices.join(", ")})`;
}

async function loadPlanejamentoBundle(planejamentoId) {
  const res = await api.get(`/planejamento-estrutura/planejamentos/${planejamentoId}/bundle`);
  const bundle = res.data || {};

  const eixos = bundle.eixos || [];
  const objetivos = bundle.objetivos || [];
  const acoes = bundle.acoes || [];
  const indicadores = bundle.indicadores || [];
  const metas = bundle.metas || [];

  const indicadoresById = {};
  indicadores.forEach(item => {
    indicadoresById[String(item.id)] = item;
  });

  return {
    planejamentoId,
    eixos,
    objetivos,
    acoes,
    indicadores,
    metas: metas.map(item => ({
      ...item,
      indicador_nome: indicadoresById[String(item.indicador_id)]?.nome || "-",
    })),
    diagnosticos: bundle.diagnosticos || [],
    riscos: bundle.riscos || [],
  };
}

export default function Dashboard() {
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [overview, setOverview] = useState(null);
  const [planejamentos, setPlanejamentos] = useState([]);
  const [detalhes, setDetalhes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [objectiveStartIndex, setObjectiveStartIndex] = useState(0);
  const [carouselDirection, setCarouselDirection] = useState("next");
  const [carouselAnimKey, setCarouselAnimKey] = useState(0);

  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get("/planejamento-estrutura/dashboard/municipios");
        const list = res.data || [];
        setWorkspaces(list);
        if (list.length > 0) {
          setWorkspaceId(String(list[0].workspace_id));
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Falha ao carregar workspaces do dashboard.");
      } finally {
        setLoading(false);
      }
    };
    loadWorkspaces();
  }, []);

  useEffect(() => {
    const loadWorkspaceData = async () => {
      if (!workspaceId) {
        return;
      }
      try {
        setLoading(true);
        setError("");

        const [overviewRes, planejamentoRes] = await Promise.all([
          api.get(`/planejamento-estrutura/dashboard/workspaces/${workspaceId}`),
          api.get(`/planejamentos/workspaces/${workspaceId}`),
        ]);

        const planejamentosList = planejamentoRes.data || [];
        setOverview(overviewRes.data || null);
        setPlanejamentos(planejamentosList);

        const detailsSettled = await Promise.allSettled(
          planejamentosList.map(item => loadPlanejamentoBundle(item.id))
        );

        const completeDetails = detailsSettled
          .filter(item => item.status === "fulfilled")
          .map(item => item.value);

        setDetalhes(completeDetails);
      } catch (err) {
        setError(err?.response?.data?.detail || "Falha ao carregar visão do workspace.");
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaceData();
  }, [workspaceId]);

  useEffect(() => {
    setObjectiveStartIndex(0);
  }, [workspaceId]);

  const selectedWorkspaceName = useMemo(() => {
    return workspaces.find(item => String(item.workspace_id) === String(workspaceId))?.workspace_nome || "-";
  }, [workspaceId, workspaces]);

  const aggregates = useMemo(() => {
    const totalEixos = detalhes.reduce((sum, item) => sum + item.eixos.length, 0);
    const totalObjetivos = detalhes.reduce((sum, item) => sum + item.objetivos.length, 0);
    const totalAcoes = detalhes.reduce((sum, item) => sum + item.acoes.length, 0);
    const totalIndicadores = detalhes.reduce((sum, item) => sum + item.indicadores.length, 0);
    const totalMetas = detalhes.reduce((sum, item) => sum + item.metas.length, 0);
    const metasConcluidas = detalhes.reduce(
      (sum, item) => sum + item.metas.filter(meta => isConcluida(meta.status)).length,
      0
    );
    const totalRiscos = detalhes.reduce((sum, item) => sum + item.riscos.length, 0);
    const riscosCriticos = detalhes.reduce(
      (sum, item) => sum + item.riscos.filter(risco => isRiscoCritico(risco.impacto)).length,
      0
    );

    const acoesNaoIniciadas = detalhes.reduce(
      (sum, item) => sum + item.acoes.filter(acao => isAcaoNaoIniciada(acao.status)).length,
      0
    );
    const acoesAndamento = detalhes.reduce(
      (sum, item) => sum + item.acoes.filter(acao => isAcaoAndamento(acao.status)).length,
      0
    );
    const acoesConcluidas = detalhes.reduce(
      (sum, item) => sum + item.acoes.filter(acao => isConcluida(acao.status)).length,
      0
    );

    const indicadoresComMetaConcluida = detalhes.reduce((sum, item) => {
      const metasByIndicador = {};
      (item.metas || []).forEach(meta => {
        const key = String(meta.indicador_id || "");
        metasByIndicador[key] = metasByIndicador[key] || [];
        metasByIndicador[key].push(meta);
      });
      const count = (item.indicadores || []).filter(ind => {
        const metas = metasByIndicador[String(ind.id)] || [];
        return metas.some(meta => isConcluida(meta.status));
      }).length;
      return sum + count;
    }, 0);

    return {
      totalEixos,
      totalObjetivos,
      totalAcoes,
      totalIndicadores,
      totalMetas,
      metasConcluidas,
      totalRiscos,
      riscosCriticos,
      acoesNaoIniciadas,
      acoesAndamento,
      acoesConcluidas,
      indicadoresComMetaConcluida,
    };
  }, [detalhes]);

  const planejamentoRows = useMemo(() => {
    const byId = new Map(detalhes.map(item => [String(item.planejamentoId), item]));
    return planejamentos.map(item => {
      const detail = byId.get(String(item.id));
      return {
        id: item.id,
        nome: item.nome,
        statusId: item.status_id,
        acoes: detail?.acoes.length || 0,
        metas: detail?.metas.length || 0,
        metasConcluidas: detail?.metas.filter(meta => isConcluida(meta.status)).length || 0,
        riscosCriticos: detail?.riscos.filter(risco => isRiscoCritico(risco.impacto)).length || 0,
      };
    });
  }, [detalhes, planejamentos]);

  const acoesRows = useMemo(() => {
    const planejamentoNomeById = new Map(
      planejamentos.map(item => [String(item.id), item.nome || `Planejamento ${item.id}`])
    );
    const rows = [];

    detalhes.forEach(detail => {
      const objetivoNomeById = new Map(
        (detail.objetivos || []).map(obj => [String(obj.id), obj.nome || `Objetivo ${obj.id}`])
      );
      const planejamentoNome =
        planejamentoNomeById.get(String(detail.planejamentoId)) || `Planejamento ${detail.planejamentoId}`;

      (detail.acoes || []).forEach(acao => {
        rows.push({
          id: acao.id,
          planejamentoNome,
          objetivoNome: objetivoNomeById.get(String(acao.objetivo_id)) || `Objetivo ${acao.objetivo_id || "-"}`,
          oQue: acao.o_que || "-",
          porQue: acao.por_que || "-",
          onde: acao.onde || "-",
          quem: acao.quem || "-",
          como: acao.como || "-",
          quando: acao.quando || "-",
          quanto: acao.quanto || "-",
          status: formatStatusLabel(acao.status),
        });
      });
    });

    return rows.sort((a, b) => Number(a.id) - Number(b.id));
  }, [detalhes, planejamentos]);

  const objectiveStatusCharts = useMemo(() => {
    const planejamentoNomeById = new Map(
      planejamentos.map(item => [String(item.id), item.nome || ("Planejamento " + item.id)])
    );
    const rows = [];

    detalhes.forEach(detail => {
      const objetivoNomeById = new Map(
        (detail.objetivos || []).map(obj => [String(obj.id), obj.nome || ("Objetivo " + obj.id)])
      );
      const planejamentoNome =
        planejamentoNomeById.get(String(detail.planejamentoId)) || ("Planejamento " + detail.planejamentoId);

      const objectiveStatusMap = {};
      (detail.acoes || []).forEach(acao => {
        const objetivoId = String(acao.objetivo_id || "");
        if (!objetivoId) return;

        if (!objectiveStatusMap[objetivoId]) {
          objectiveStatusMap[objetivoId] = { naoIniciadas: 0, andamento: 0, concluidas: 0 };
        }

        if (isAcaoNaoIniciada(acao.status)) {
          objectiveStatusMap[objetivoId].naoIniciadas += 1;
          return;
        }

        if (isAcaoAndamento(acao.status)) {
          objectiveStatusMap[objetivoId].andamento += 1;
          return;
        }

        if (isConcluida(acao.status)) {
          objectiveStatusMap[objetivoId].concluidas += 1;
          return;
        }

        objectiveStatusMap[objetivoId].naoIniciadas += 1;
      });

      Object.entries(objectiveStatusMap).forEach(([objetivoId, counts]) => {
        const statusItems = [
          { key: "nao_iniciadas", label: "N\u00e3o iniciadas", value: counts.naoIniciadas, color: "#E69982" },
          { key: "andamento", label: "Em andamento", value: counts.andamento, color: "#D23716" },
          { key: "concluidas", label: "Conclu\u00eddas", value: counts.concluidas, color: "#63130F" },
        ];
        const total = statusItems.reduce((sum, item) => sum + item.value, 0);
        if (total <= 0) {
          return;
        }

        rows.push({
          key: String(detail.planejamentoId) + "-" + objetivoId,
          planejamentoNome,
          objetivoNome: objetivoNomeById.get(objetivoId) || ("Objetivo " + objetivoId),
          totalAcoes: total,
          statusItems,
          gradient: buildConicGradient(statusItems, total),
        });
      });
    });

    return rows.sort((a, b) => {
      const objetivoCompare = a.objetivoNome.localeCompare(b.objetivoNome, "pt-BR");
      if (objetivoCompare !== 0) return objetivoCompare;
      return a.planejamentoNome.localeCompare(b.planejamentoNome, "pt-BR");
    });
  }, [detalhes, planejamentos]);

  const objectiveChartsVisible = useMemo(() => {
    if (objectiveStatusCharts.length === 0) {
      return [];
    }

    const total = objectiveStatusCharts.length;
    const start = objectiveStartIndex % total;

    return Array.from({ length: OBJECTIVE_CAROUSEL_SIZE }, (_, offset) => {
      const index = (start + offset) % total;
      return objectiveStatusCharts[index];
    });
  }, [objectiveStartIndex, objectiveStatusCharts]);

  useEffect(() => {
    if (objectiveStatusCharts.length === 0) {
      setObjectiveStartIndex(0);
      return;
    }
    setObjectiveStartIndex(prev => prev % objectiveStatusCharts.length);
  }, [objectiveStatusCharts.length]);

  const handleObjectiveNext = () => {
    const total = objectiveStatusCharts.length;
    if (total === 0) {
      return;
    }
    setCarouselDirection("next");
    setCarouselAnimKey(prev => prev + 1);
    setObjectiveStartIndex(prev => (prev + 1) % total);
  };

  const handleObjectivePrev = () => {
    const total = objectiveStatusCharts.length;
    if (total === 0) {
      return;
    }
    setCarouselDirection("prev");
    setCarouselAnimKey(prev => prev + 1);
    setObjectiveStartIndex(prev => (prev - 1 + total) % total);
  };

  const metasPorPlanejamento = useMemo(() => {
    return planejamentoRows
      .map(item => ({
        ...item,
        percentual: item.metas > 0 ? Math.round((item.metasConcluidas / item.metas) * 100) : 0,
      }))
      .sort((a, b) => b.percentual - a.percentual)
      .slice(0, 6);
  }, [planejamentoRows]);

  const riscosPorPlanejamento = useMemo(() => {
    return planejamentoRows
      .filter(item => item.riscosCriticos > 0)
      .sort((a, b) => b.riscosCriticos - a.riscosCriticos)
      .slice(0, 6);
  }, [planejamentoRows]);

  const riscoMax = useMemo(() => {
    return riscosPorPlanejamento.reduce((max, item) => Math.max(max, item.riscosCriticos), 0);
  }, [riscosPorPlanejamento]);

  const kpis = useMemo(
    () => [
      {
        label: "Planejamentos",
        value: overview?.planejamentos ?? planejamentos.length,
        helper: `${selectedWorkspaceName}`,
      },
      {
        label: "Eixos",
        value: aggregates.totalEixos,
        helper: "Estruturas estratégicas",
      },
      {
        label: "Objetivos",
        value: aggregates.totalObjetivos,
        helper: "Objetivos cadastrados",
      },
      {
        label: "Ações",
        value: aggregates.totalAcoes,
        helper: `${aggregates.acoesConcluidas} concluídas`,
      },
      {
        label: "Metas concluídas",
        value: `${aggregates.metasConcluidas}/${aggregates.totalMetas}`,
        helper: "Itens de meta concluídos",
      },
      {
        label: "Riscos críticos",
        value: aggregates.riscosCriticos,
        helper: `${aggregates.totalRiscos} riscos mapeados`,
      },
      {
        label: "Ações não iniciadas",
        value: aggregates.acoesNaoIniciadas,
        helper: "Status em Não iniciado",
      },
      {
        label: "Indicadores com metas concluídas",
        value: `${aggregates.indicadoresComMetaConcluida}/${aggregates.totalIndicadores}`,
        helper: "Com ao menos uma meta concluída",
      },
    ],
    [aggregates, overview, planejamentos.length, selectedWorkspaceName]
  );

  return (
    <main className="dashboard-page">
      <header className="dash-topbar">
        <div>
          <p className="dash-kicker">Magma Planning</p>
          <h1 className="dash-title">Painel de Gestão do Workspace</h1>
          <p className="dash-subtitle">Visão consolidada de estrutura, execução e riscos dos planejamentos.</p>
        </div>
        <div className="dash-filter-box">
          <label htmlFor="workspace">Workspace</label>
          <MagmaSelect
            id="workspace"
            className="dash-select"
            value={workspaceId}
            onChange={e => setWorkspaceId(e.target.value)}
          >
            {workspaces.map(item => (
              <option key={item.workspace_id} value={item.workspace_id}>
                {item.workspace_nome}
              </option>
            ))}
          </MagmaSelect>
        </div>
      </header>

      {loading ? <p className="dash-note">Carregando dados do workspace...</p> : null}
      {error ? <p className="dash-error">{error}</p> : null}

      <section className="dash-grid dash-grid-kpis">
        {kpis.map(card => (
          <article key={card.label} className="dash-card dash-kpi">
            <p className="dash-kpi-label">{card.label}</p>
            <p className="dash-kpi-value">{card.value}</p>
            <p className="dash-kpi-delta">{card.helper}</p>
          </article>
        ))}
      </section>

      <section className="dash-grid dash-grid-main">
        <article className="dash-card">
          <div className="dash-carousel-head">
            <h2 className="dash-section-title">{"Status das A\u00e7\u00f5es por Objetivo"}</h2>
          </div>

          {objectiveStatusCharts.length === 0 ? (
            <p className="dash-note">{"Nenhuma a\u00e7\u00e3o encontrada para gerar gr\u00e1ficos por objetivo."}</p>
          ) : (
            <div className="dash-carousel-shell">
              <button
                type="button"
                className="dash-carousel-arrow"
                onClick={handleObjectivePrev}
                aria-label="Gr\u00e1ficos anteriores"
              >
                {String.fromCharCode(8592)}
              </button>

              <div className="dash-carousel-viewport">
                <div
                  key={carouselAnimKey}
                  className={`dash-objective-grid ${
                    carouselDirection === "next" ? "is-slide-next" : "is-slide-prev"
                  }`}
                >
                  {objectiveChartsVisible.map((chart, index) => (
                    <article key={`${chart.key}-${index}`} className="dash-objective-card">
                      <p className="dash-objective-plan">{chart.planejamentoNome}</p>
                      <h3 className="dash-objective-title">{chart.objetivoNome}</h3>

                      <div className="dash-donut" style={{ background: chart.gradient }}>
                        <div className="dash-donut-hole">
                          <strong>{chart.totalAcoes}</strong>
                          <span>{"A\u00e7\u00f5es"}</span>
                        </div>
                      </div>

                      <ul className="dash-legend">
                        {chart.statusItems.map(item => (
                          <li key={item.key}>
                            <span className="dash-legend-main">
                              <span className="dash-legend-swatch" style={{ backgroundColor: item.color }} />
                              {item.label}
                            </span>
                            <strong>{item.value}</strong>
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="dash-carousel-arrow"
                onClick={handleObjectiveNext}
                aria-label="Pr\u00f3ximos gr\u00e1ficos"
              >
                {String.fromCharCode(8594)}
              </button>
            </div>
          )}
        </article>

        <article className="dash-card">
          <h2 className="dash-section-title">Metas concluídas por planejamento</h2>
          <div className="dash-metric-bars">
            {metasPorPlanejamento.map(item => (
              <div className="dash-metric-row" key={item.id}>
                <div className="dash-metric-head">
                  <span>{item.nome}</span>
                  <strong>{item.percentual}%</strong>
                </div>
                <div className="dash-metric-track">
                  <div className="dash-metric-fill" style={{ width: `${item.percentual}%` }} />
                </div>
                <p className="dash-metric-meta">
                  {item.metasConcluidas}/{item.metas} metas concluídas
                </p>
              </div>
            ))}
            {metasPorPlanejamento.length === 0 ? (
              <p className="dash-note">Sem metas suficientes para montar o gráfico.</p>
            ) : null}
          </div>
        </article>
      </section>

      <section className="dash-grid">
        <article className="dash-card">
          <h2 className="dash-section-title">Riscos críticos por planejamento</h2>
          <div className="dash-risk-bars">
            {riscosPorPlanejamento.map(item => (
              <div className="dash-risk-row" key={item.id}>
                <span className="dash-risk-label">{item.nome}</span>
                <div className="dash-risk-track">
                  <div
                    className="dash-risk-fill"
                    style={{
                      width: `${riscoMax > 0 ? (item.riscosCriticos / riscoMax) * 100 : 0}%`,
                    }}
                  />
                </div>
                <strong className="dash-risk-value">{item.riscosCriticos}</strong>
              </div>
            ))}
            {riscosPorPlanejamento.length === 0 ? (
              <p className="dash-note">Nenhum risco crítico encontrado no workspace selecionado.</p>
            ) : null}
          </div>
        </article>
      </section>

      <section className="dash-grid dash-grid-main">
        <article className="dash-card dash-card-full">
          <h2 className="dash-section-title">Ações do workspace</h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Planejamento</th>
                  <th>Objetivo</th>
                  <th>O que</th>
                  <th>Por que</th>
                  <th>Onde</th>
                  <th>Quem</th>
                  <th>Como</th>
                  <th>Quando</th>
                  <th>Quanto</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {acoesRows.map(item => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.planejamentoNome}</td>
                    <td>{item.objetivoNome}</td>
                    <td>{item.oQue}</td>
                    <td>{item.porQue}</td>
                    <td>{item.onde}</td>
                    <td>{item.quem}</td>
                    <td>{item.como}</td>
                    <td>{item.quando}</td>
                    <td>{item.quanto}</td>
                    <td>{item.status}</td>
                  </tr>
                ))}
                {acoesRows.length === 0 ? (
                  <tr>
                    <td colSpan="11">Nenhuma ação encontrada neste workspace.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="dash-grid">
        <article className="dash-card">
          <h2 className="dash-section-title">Planejamentos no workspace</h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Planejamento</th>
                  <th>Ações</th>
                  <th>Metas</th>
                  <th>Riscos críticos</th>
                </tr>
              </thead>
              <tbody>
                {planejamentoRows.map(item => (
                  <tr key={item.id}>
                    <td>
                      <Link className="dash-inline-link" to={`/planejamentos/${item.id}`}>
                        {item.nome}
                      </Link>
                    </td>
                    <td>{item.acoes}</td>
                    <td>
                      {item.metasConcluidas}/{item.metas}
                    </td>
                    <td>{item.riscosCriticos}</td>
                  </tr>
                ))}
                {planejamentoRows.length === 0 ? (
                  <tr>
                    <td colSpan="4">Nenhum planejamento encontrado neste workspace.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="dash-grid dash-grid-main">
        <article className="dash-card">
          <h2 className="dash-section-title">Progresso por planejamento</h2>
          <div className="dash-bars">
            {(overview?.progresso_planejamentos || []).map(item => (
              <div className="dash-bar-row" key={item.planejamento_id}>
                <div className="dash-bar-head">
                  <span>{item.nome}</span>
                  <strong>{item.progresso}%</strong>
                </div>
                <div className="dash-bar-track">
                  <div className="dash-bar-fill" style={{ width: `${item.progresso}%` }} />
                </div>
              </div>
            ))}
            {(overview?.progresso_planejamentos || []).length === 0 ? (
              <p className="dash-note">Sem progresso disponível para exibir.</p>
            ) : null}
          </div>
        </article>

        <article className="dash-card">
          <h2 className="dash-section-title">Visão por workspace</h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Planejamentos</th>
                  <th>Riscos críticos</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map(item => (
                  <tr key={item.workspace_id}>
                    <td>{item.workspace_nome}</td>
                    <td>{item.planejamentos}</td>
                    <td>{item.riscos_criticos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </main>
  );
}
